/**
 * Scanner de code-barres — portage du prototype de l'étape 2 en composant React
 * (ARCHITECTURE.md §2 et §6.2, specs §6.4).
 *
 * ⚠️ **Trois acquis du prototype sont repris, et rien d'autre.** `proto/scanner/`
 * était un banc d'essai — options de décodage, mesures, journal — pas une base
 * de code à porter telle quelle. Ce qui traverse :
 *
 * 1. **`locateFile` surchargé** — par défaut `zxing-wasm` va chercher son
 *    binaire sur le CDN jsDelivr. On le sert depuis notre propre domaine :
 *    aucune dépendance réseau tierce (§5.2), et le scan fonctionne même si
 *    jsDelivr est inaccessible.
 * 2. **Bande d'intérêt centrée** — un EAN-13 est linéaire : analyser toute
 *    l'image coûterait cher pour rien. Le cadre affiché correspond **au pixel
 *    près** à la zone envoyée au décodeur, sinon viser devient un jeu de hasard.
 * 3. **Libération du `MediaStream` sur les trois chemins de sortie** — arrêt
 *    explicite, page masquée, démontage. Le §2 est formel : sans `track.stop()`,
 *    la LED caméra reste allumée et la batterie se vide.
 *
 * ⚠️ **`zxing-wasm` est chargé en import dynamique** (§6.2), à l'ouverture du
 * scanner et pas avant. Le `.wasm` pèse 1,04 Mo à lui seul, contre ~120 ko pour
 * tout le bundle du front : le mettre sur le chemin de démarrage de l'app serait
 * la ralentir dix fois pour une fonction utilisée quelques secondes par jour.
 *
 * ⚠️ **La caméra ne s'ouvre que sur geste explicite** (§2). Ce composant n'est
 * monté que lorsque l'onglet « Scanner » est sélectionné dans une feuille déjà
 * ouverte : deux taps délibérés. Il ne doit jamais être monté « au cas où » —
 * la feuille d'ajout, elle, est préchargée en permanence (§6.2), et un scanner
 * monté avec elle demanderait la caméra au lancement de l'application.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { eanChecksumValid } from '../../lib/barcode';
import { TOKENS } from '../ui/tokens';
import { Button, ErrorNote, Hint } from '../ui/primitives';

// ⚠️ Import d'**URL** seulement : Vite émet le binaire comme un asset haché et
// n'inscrit ici qu'une chaîne. Le `.wasm` n'est téléchargé que lorsque le module
// zxing le demande, c'est-à-dire au premier démarrage du scanner. Le hachage lui
// vaut le `Cache-Control: immutable` de `/assets/*` posé par Caddy (§7.4).
import wasmUrl from 'zxing-wasm/reader/zxing_reader.wasm?url';

/* ==========================================================================
 * Réglages du décodage — repris du prototype
 * ======================================================================= */

/** Bande centrale envoyée au décodeur, en fraction de l'image (§2, acquis n°2). */
const ROI = { width: 0.9, height: 0.38 } as const;

/** Largeur maximale analysée : ~6 px par module fin suffisent à un EAN-13. */
const MAX_CAPTURE_WIDTH = 1280;

/** Plafond de surface — borne le coût d'une analyse quand le flux est en portrait. */
const MAX_CAPTURE_PIXELS = 480_000;

/** Pause entre deux analyses, pour laisser respirer le fil principal. */
const LOOP_GAP_MS = 35;

/**
 * Nombre de lectures **identiques consécutives** exigées avant d'accepter un code.
 *
 * ⚠️ Ajouté par rapport au prototype, et pas par excès de prudence : le
 * prototype affichait un code, ici on va **écrire dans le journal**. Une lecture
 * isolée peut être un artefact d'optique ou de flou de bougé ; deux lectures
 * identiques d'affilée coûtent moins de 100 ms et rendent le faux positif
 * négligeable. La somme de contrôle EAN, vérifiée en plus, ne couvre pas tout :
 * un artefact peut la satisfaire par hasard une fois sur dix.
 */
const CONFIRMATIONS_REQUIRED = 2;

type Phase = 'starting' | 'running' | 'error';

/** Message lisible pour chaque refus de `getUserMedia`. */
function cameraErrorMessage(error: unknown): string {
  const name = error instanceof Error ? error.name : 'Error';
  switch (name) {
    case 'NotAllowedError':
      return 'Accès à la caméra refusé. Dans Safari : « aA » dans la barre d’adresse → Réglages du site → Caméra → Autoriser.';
    case 'NotFoundError':
      return 'Aucune caméra détectée sur cet appareil.';
    case 'NotReadableError':
      return 'La caméra est déjà utilisée par une autre application.';
    case 'OverconstrainedError':
      return 'Aucune caméra ne satisfait les contraintes demandées.';
    case 'SecurityError':
      return 'Accès bloqué : la page doit être servie en HTTPS.';
    default:
      return error instanceof Error ? error.message : 'Échec de l’accès à la caméra.';
  }
}

export function BarcodeScanner({
  onDetected,
  hint,
}: {
  /** Appelé une fois, avec un code confirmé. Le scanner s'arrête juste après. */
  onDetected: (code: string) => void;
  hint?: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const runningRef = useRef(false);
  const [phase, setPhase] = useState<Phase>('starting');
  const [message, setMessage] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  /**
   * Libération du flux — **le point de vigilance n°1 du §2**.
   *
   * Idempotente : elle est appelée depuis les trois chemins de sortie, et deux
   * d'entre eux peuvent se produire coup sur coup (masquer la page **puis**
   * démonter le composant).
   */
  const releaseStream = useCallback(() => {
    runningRef.current = false;
    const stream = streamRef.current;
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      streamRef.current = null;
    }
    const video = videoRef.current;
    if (video?.srcObject) video.srcObject = null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    /**
     * Bande centrale de l'image vidéo, réduite si besoin — acquis n°2.
     * La géométrie est identique à celle du cadre affiché, à la fraction près.
     */
    const grabRegion = (video: HTMLVideoElement): ImageData | null => {
      if (!context) return null;
      const { videoWidth, videoHeight } = video;
      if (videoWidth === 0 || videoHeight === 0) return null;

      const sourceWidth = Math.round(videoWidth * ROI.width);
      const sourceHeight = Math.round(videoHeight * ROI.height);
      const sourceX = Math.round((videoWidth - sourceWidth) / 2);
      const sourceY = Math.round((videoHeight - sourceHeight) / 2);

      const scale = Math.min(
        1,
        MAX_CAPTURE_WIDTH / sourceWidth,
        Math.sqrt(MAX_CAPTURE_PIXELS / (sourceWidth * sourceHeight)),
      );
      const width = Math.max(1, Math.round(sourceWidth * scale));
      const height = Math.max(1, Math.round(sourceHeight * scale));

      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
      }
      context.drawImage(
        video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, width, height,
      );
      return context.getImageData(0, 0, width, height);
    };

    const start = async () => {
      setPhase('starting');
      setMessage(null);

      // La caméra est demandée en premier — au plus près du geste utilisateur —
      // et le module WASM se charge en parallèle, pendant que le capteur
      // s'initialise. C'est ce qui fait tenir la cible des 5 secondes (§6.4).
      const cameraPromise = navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      // ⚠️ Import dynamique (§6.2) : ni le module ni le `.wasm` ne sont chargés
      // avant ce point. `locateFile` est surchargé pour servir le binaire depuis
      // notre domaine — acquis n°1 du prototype.
      const modulePromise = import('zxing-wasm/reader').then(async (zxing) => {
        await zxing.prepareZXingModule({
          overrides: { locateFile: (path: string, prefix: string) =>
            path.endsWith('.wasm') ? wasmUrl : prefix + path },
          fireImmediately: true,
        });
        return zxing;
      });

      let stream: MediaStream;
      try {
        stream = await cameraPromise;
      } catch (error) {
        modulePromise.catch(() => undefined);
        if (cancelled) return;
        setPhase('error');
        setMessage(cameraErrorMessage(error));
        return;
      }

      // Course perdue : le composant a été démonté pendant l'autorisation. Le
      // flux vient d'être ouvert et personne ne le fermera — sauf ici.
      if (cancelled) {
        for (const track of stream.getTracks()) track.stop();
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        releaseStream();
        return;
      }
      video.srcObject = stream;

      try {
        await video.play();
      } catch (error) {
        releaseStream();
        if (cancelled) return;
        setPhase('error');
        setMessage(`La vidéo n’a pas démarré : ${String(error)}`);
        return;
      }

      let zxing: Awaited<typeof modulePromise>;
      try {
        zxing = await modulePromise;
      } catch (error) {
        releaseStream();
        if (cancelled) return;
        setPhase('error');
        setMessage(`Le décodeur n’a pas pu être chargé : ${String(error)}`);
        return;
      }

      if (cancelled) {
        releaseStream();
        return;
      }

      setPhase('running');
      runningRef.current = true;

      let lastCode: string | null = null;
      let repeats = 0;

      while (runningRef.current && !cancelled) {
        try {
          if (video.readyState >= 2) {
            const image = grabRegion(video);
            if (image) {
              const results = await zxing.readBarcodes(image, {
                // EAN-13 seul : c'est le format des produits alimentaires
                // européens. EAN-8 et UPC-A suivent, pour les petits formats et
                // les produits importés — les ajouter coûte peu et évite un
                // « ça ne marche pas » sur une boîte de conserve américaine.
                formats: ['EAN-13', 'EAN-8', 'UPC-A'],
                tryHarder: true,
                tryRotate: false,
                tryInvert: false,
                tryDownscale: true,
                maxNumberOfSymbols: 1,
              });

              for (const result of results) {
                if (!result.isValid || result.text === '') continue;
                if (eanChecksumValid(result.text) === false) continue;

                repeats = result.text === lastCode ? repeats + 1 : 1;
                lastCode = result.text;

                if (repeats >= CONFIRMATIONS_REQUIRED) {
                  runningRef.current = false;
                  releaseStream();
                  if (!cancelled) onDetected(result.text);
                  return;
                }
              }
            }
          }
        } catch {
          // Une trame illisible n'est pas un incident : on passe à la suivante.
        }
        await new Promise((resolve) => setTimeout(resolve, LOOP_GAP_MS));
      }
    };

    void start();

    /* --- Sortie n°2 : page masquée --------------------------------------
     * L'utilisateur bascule d'application ou verrouille l'iPhone. Sans ce
     * relâchement, la caméra reste ouverte en arrière-plan. */
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        releaseStream();
        setPhase('error');
        setMessage('Caméra libérée : l’application est passée en arrière-plan.');
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    /* --- Sortie n°3 : démontage du composant ---------------------------- */
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      releaseStream();
    };
    // `attempt` relance l'effet sur « Réessayer », ce qui redémarre proprement
    // (le nettoyage ci-dessus a libéré le flux précédent).
  }, [onDetected, releaseStream, attempt]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '3 / 4',
          maxHeight: '46dvh',
          /*
            ⚠️ Noir en dur, et **volontairement hors thème** : ce n'est pas une
            surface de l'application mais le fond du viseur, derrière l'image de
            la caméra. Un viseur est noir dans les deux thèmes — c'est ce que
            fait l'appareil photo d'iOS. Même raison pour le blanc du cadre de
            visée et du message d'ouverture : ils se posent sur l'image, pas sur
            une surface.
          */
          background: '#000',
          borderRadius: '1rem',
          overflow: 'hidden',
          border: `1px solid ${TOKENS.border}`,
        }}
      >
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />

        {/* Cadre de visée — même géométrie que la zone réellement décodée. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: `${String(((1 - ROI.width) / 2) * 100)}%`,
            top: `${String(((1 - ROI.height) / 2) * 100)}%`,
            width: `${String(ROI.width * 100)}%`,
            height: `${String(ROI.height * 100)}%`,
            border: '2px solid rgba(255, 255, 255, 0.9)',
            borderRadius: '0.5rem',
            boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.45)',
            pointerEvents: 'none',
          }}
        />

        {phase === 'starting' && (
          <p
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 0,
              color: '#fff',
              fontSize: '0.9rem',
              textShadow: '0 1px 3px rgba(0,0,0,0.9)',
            }}
          >
            Ouverture de la caméra…
          </p>
        )}
      </div>

      {phase === 'error' ? (
        <>
          <ErrorNote>{message}</ErrorNote>
          <Button
            variant="quiet"
            onClick={() => {
              setAttempt((value) => value + 1);
            }}
          >
            Réessayer
          </Button>
        </>
      ) : (
        <Hint>{hint ?? 'Cadrer le code-barres dans le rectangle, à 10–20 cm.'}</Hint>
      )}
    </div>
  );
}
